import { createRef, useCallback, useEffect, useRef, useState } from 'react'
import './PlayerSection.scss'
import Hand from '../Hand/Hand'
import BoardSection from '../Board/BoardSection'
import { v4 as uuidv4 } from 'uuid';
import Card from '../Card/Card';
import Deck from '../Deck/Deck';
import Discard from '../Discard/Discard';
import Stack from '../Stack/Stack';
import { hideCard, isCardHidden, setPositions, shuffleArray, swapHideOrRevealCard } from '../../Commons/Functions';
import { useGameContext } from '../../Commons/GameContext';
import { Button, FloatingLabel, Form, Modal } from 'react-bootstrap';
import { ACTION_MODES } from '../ActionMode';
import LayoutBoardSection from '../Board/LayoutBoardSection';
import DeckPickerModal from './Modals/DeckPickerModal';
import TokensModal from './Modals/TokensModal';

const defaultCard = {
    id: false,
    position: {
        section: "NONE",
        index: -1,
        reversedIndex: -1
    },
    hiddenTo: {},
    isTapped: false,
    isFlipped: false,
    cardData: {
        name: "ezfzfzef",
        id: "tergegr",
    },
    counters: [],
    notes: "",
    owner: ""
}

export const SECTIONS = {
    HAND: "Hand",
    DISCARD: "Discard",
    STACK: "Stack",
    DECK: "Deck",
    EXILE: "Exile",
    EXILE_HIDDEN: "ExileHidden",
    SIDEBOARD: "Sideboard",
    ALIGNMENTS: {
        START: "Start",
        END: "End",
        CENTER: "Center",
        NONE: "None",
        STACK: "Stack"
    },
    SECTION_SIZES: {
        SMALL: 13,
        DEFAULT: 15,
        MEDIUM: 20,
        BIG: 26,
        HUGE: 40
    }
}

export const defaultSections = {
    sections: [
        "Hand",
        "Discard",
        "Deck",
        "Stack",
        "Exile",
        "ExileHidden",
        "Sideboard"
    ],
    sectionsDict: {
        "Hand": {
            "isDefaultSection": true,
            "title": "Hand",
            "isHidden": "opponent-only",
            "height": "DEFAULT",
            "alignment": "CENTER",
            "autoPayFrom": true,
            "isHorizontalAllowed": false,
            "isGroupForbidden": true
        },
        "Discard": {
            "isDefaultSection": true,
            "title": "Discard",
            "isHidden": "no",
            "height": "DEFAULT",
            "alignment": "NONE",
            "isHorizontalAllowed": false,
            "isGroupForbidden": true,
            "noAutoPayTo": true
        },
        "Deck": {
            "isDefaultSection": true,
            "title": "Deck",
            "isHidden": "yes",
            "height": "DEFAULT",
            "alignment": "NONE",
            "isHorizontalAllowed": false,
            "isGroupForbidden": true
        },
        "Stack": {
            "isDefaultSection": true,
            "title": "Stack",
            "isHidden": "no",
            "height": "HUGE",
            "alignment": "NONE",
            "isHorizontalAllowed": false,
            "isGroupForbidden": true,
            "displayedTitle": "Pile"
        },
        "Exile": {
            "isDefaultSection": true,
            "title": "Exile",
            "isHidden": "yes",
            "height": "DEFAULT",
            "alignment": "NONE",
            "isHorizontalAllowed": false,
            "isGroupForbidden": true,
            "noAutoPayTo": true
        },
        "ExileHidden": {
            "isDefaultSection": true,
            "title": "ExileHidden",
            "isHidden": "yes",
            "height": "DEFAULT",
            "alignment": "NONE",
            "isHorizontalAllowed": false,
            "isGroupForbidden": true,
            "noAutoPayTo": true
        },
        "Sideboard": {
            "isDefaultSection": true,
            "title": "Sideboard",
            "isHidden": "yes",
            "height": "DEFAULT",
            "alignment": "NONE",
            "isHorizontalAllowed": false,
            "isGroupForbidden": true
        }
    }
}

export default function PlayerSection(props) {
    const { gameCards, gameData, currentGame, cardSize } = useGameContext()
    const [deck, setDeck] = useState([])
    const [sideboard, setSideboard] = useState([])
    const [topCardHiddenTo, setTopCardHiddenTo] = useState({})
    const topCardHiddenToRef = useRef({})
    const playerDeckRef = useRef([])
    const [visibleCards, setVisibleCards] = useState([])
    const visibleCardsRef = useRef([])

    const cardDraggedRef = useRef(false)
    const handRef = useRef(null)
    const deckRef = useRef(null)
    const discardRef = useRef(null)

    const [sections, setSections] = useState(false)
    const sectionsCardsRef = useRef({})

    const [opponentStackCards, setOpponentStackCards] = useState([])
    const [latestStackCard, setLatestSackCard] = useState(false)

    const [availableTokens, setAvailableTokens] = useState([])

    const [showDeckPickerModal, setShowDeckPickerModal] = useState(false)

    const [hoverCardCost, setHoverCardCost] = useState(0)

    const refreshCount = useRef(0)

    function createGameCardFromCardData(c) {
        let newCard = { ...defaultCard }
        newCard.cardData = { ...c }
        newCard.hiddenTo = JSON.parse(JSON.stringify({}))
        newCard.isHorizontal = false
        delete newCard.cardData.count
        newCard.counters = [{}, {}]
        newCard.id = uuidv4()
        newCard.owner = props.playerId
        return newCard
    }

    function saveGameData() {
        return {
            deck: playerDeckRef.current,
            visibleCards: visibleCardsRef.current,
        };
    }

    function loadGameData(data) {
        if (!data) return;

        let deckCards = data.deck || []
        deckCards.forEach((c) => {
            c.owner = props.playerId
        })

        setDeck(deckCards);
        playerDeckRef.current = deckCards

        let cards = data.visibleCards || []
        cards.forEach((c) => {
            c.owner = props.playerId
            if (Object.keys(c.hiddenTo).length !== 0 || c.position.section === SECTIONS.HAND) {
                hideCard(c, "opponent-only", props.playerId, props.opponentIds)
            }
        })
        setVisibleCards(cards);
        visibleCardsRef.current = cards

        repositionCards()
    }

    /*------------------------------- USE EFFECTS -------------------------------*/

    useEffect(() => {
        if (props.gameOptions) {
            const sectionsFile = props.gameOptions.format.sections

            // SETUP SECTIONS
            let newSections = {
                allSections: [...sectionsFile.customSections, ...defaultSections.sections],
                customSections: [...sectionsFile.customSections],
                layout: { ...sectionsFile.layout },
                autoPlayFromHand: sectionsFile.autoPlayFromHand,
                autoPlayFromStack: sectionsFile.autoPlayFromStack,
                sectionsDict: {},
            }
            sectionsFile.sectionsDict = { ...defaultSections.sectionsDict, ...sectionsFile.sectionsDict }
            newSections.allSections.forEach((key) => {
                let section = sectionsFile.sectionsDict[key]
                let ref = null

                if (section.isDefaultSection) {
                    if (key == SECTIONS.HAND) {
                        ref = handRef
                    } else if (key == SECTIONS.DISCARD || key == SECTIONS.EXILE || key == SECTIONS.EXILE_HIDDEN || key == SECTIONS.SIDEBOARD) {
                        ref = discardRef
                    } else if (key == SECTIONS.STACK) {
                        ref = props.stackRef
                    } else if (key == SECTIONS.DECK) {
                        ref = deckRef
                    }
                } else {
                    ref = createRef()
                }

                newSections.sectionsDict[key] = {
                    title: section.title,
                    isHidden: section.isHidden,
                    sectionRef: ref,
                    sectionBoundingClientRect: false,
                    height: SECTIONS.SECTION_SIZES[section.height],
                    alignment: SECTIONS.ALIGNMENTS[section.alignment],
                    cardHeight: SECTIONS.SECTION_SIZES[section.height],
                    cardWidth: SECTIONS.SECTION_SIZES[section.height] * 0.7,
                    autoPayFrom: section.autoPayFrom,
                    noAutoPayTo: section.noAutoPayTo,
                    enterTapped: section.enterTapped,
                    enterSpun: section.enterSpun,
                    isHorizontalAllowed: section.isHorizontalAllowed,
                    isGroupForbidden: section.isGroupForbidden,
                    keepTappedNewTurn: section.keepTappedNewTurn,
                    displayedTitle: section.displayedTitle,
                    showHiddenCardInHistory: section.showHiddenCardInHistory,
                    noQuickActions: section.noQuickActions
                }
            })

            newSections.shouldUpdateClientRect = true
            setSections(newSections)
            setShowDeckPickerModal(true)
            visibleCardsRef.current = []
            setVisibleCards([])
            setOpponentStackCards([])
            sectionsCardsRef.current = {}
            setDeck([])
            playerDeckRef.current = []

            let deckTopHidden = {}
            let tmp = [...props.opponentIds, props.playerId]
            tmp.forEach(id => {
                deckTopHidden[id] = true
            })
            setTopCardHiddenTo(deckTopHidden)
            sendData([], [], deckTopHidden)
        }
    }, [props.gameOptions])

    const prevOpponentIdsRef = useRef([]);

    useEffect(() => {
        const prevIds = prevOpponentIdsRef.current;
        const currentIds = props.opponentIds;

        if (currentIds && currentIds.length > prevIds.length) {
            const newIds = currentIds.filter(id => !prevIds.includes(id));

            if (newIds.length > 0 && props.gameOptions) {
                if (visibleCardsRef.current.length === 0 && playerDeckRef.current.length === 0) {
                    //props.sendToHistory(`I joined started game`, false, false, true);
                    const saved = localStorage.getItem("savedGame_" + gameData.name);
                    if (saved) {
                        loadGameData(JSON.parse(saved));
                        setShowDeckPickerModal(false)
                    }
                } else {
                    const newPlayerId = newIds[0]
                    //props.sendToHistory(`new player (${newPlayerId}) joined my game, updating visible cards`, false, false, true);
                    visibleCardsRef.current.forEach((c) => {
                        if (Object.keys(c.hiddenTo).length !== 0 || c.position.section === SECTIONS.HAND) {
                            c.hiddenTo[newPlayerId] = true
                        }
                    })
                    let tmp = { ...topCardHiddenTo }
                    tmp[newPlayerId] = true
                    setTopCardHiddenTo(tmp)
                    repositionCards()
                }
            }
        }

        prevOpponentIdsRef.current = currentIds;
    }, [props.opponentIds]);

    useEffect(() => {
        if (props.turnCount > 1) {
            let saved = localStorage.getItem("savedGame_" + gameData.name);
            let newSavedData = saveGameData()
            if (saved) {
                saved = JSON.parse(saved)
                newSavedData = {
                    ...saved,
                    ...newSavedData
                }
            }
            localStorage.setItem("savedGame_" + gameData.name, JSON.stringify(newSavedData));
            newTurn()
        }
    }, [props.turnCount])

    useEffect(() => {
        const addGivenCard = (givenCard) => {
            let newCard = { ...givenCard }
            if (givenCard.grouppingTarget) {
                // GIVE GROUPPED
                let parentCard = false
                visibleCardsRef.current.forEach((c) => {
                    if (c.id === givenCard.grouppingTarget) {
                        parentCard = c
                    }
                })
                if (parentCard) {
                    visibleCardsRef.current.push(newCard)
                    groupCard(parentCard, newCard)
                }
                delete newCard.grouppingTarget
            } else {
                let newSection = SECTIONS.STACK
                if (newCard.position.section === SECTIONS.EXILE_HIDDEN) {
                    newSection = SECTIONS.EXILE_HIDDEN
                }
                // GIVE CLASSIC IN STACK
                let rect = sections.sectionsDict[newSection].sectionBoundingClientRect
                let stackSectionData = sections.sectionsDict[newSection]

                /*
                TODO: DELETE IF NOT NEEDED
                let allCards = [...visibleCardsRef.current, ...opponentStackCards]
                let stackCount = 0
                allCards.forEach((c, i) => {
                    if (c.position.section == SECTIONS.STACK) {
                        stackCount += 1
                    }
                })

                newCard.stackIndex = stackCount + 1
*/
                newCard.position = {
                    section: newSection,
                    pos: { left: rect.left, top: rect.top },
                    cardSize: {
                        width: newCard.isHorizontal ? stackSectionData.cardHeight : stackSectionData.cardWidth,
                        height: newCard.isHorizontal ? stackSectionData.cardWidth : stackSectionData.cardHeight
                    }
                }

                // A VOIR POUR CA PAR CONTRE
                if (newCard.stackIndex === undefined) {
                    newCard.stackIndex = 99999
                }
                delete newCard.grouppedToId

                visibleCardsRef.current.push(newCard)
            }
        }
        if (props.givenCard) {
            props.givenCard.forEach((c) => {
                addGivenCard(c)
            })

            props.setGivenCard(false)
            const rafId = requestAnimationFrame(() => {
                repositionCards()
            });

            return () => cancelAnimationFrame(rafId);
        }
    }, [props.givenCard])

    useEffect(() => {
        visibleCardsRef.current.forEach(c => {
            delete c.isPresented
        })
        const rafId = requestIdleCallback(() => {
            repositionCards()
        });

        return () => cancelIdleCallback(rafId);
    }, [props.toggleRemovePresented])

    useEffect(() => {
        if (!sections || !sections.sectionsDict[SECTIONS.STACK]) { return }
        // PEUT ETRE FAIRE MIEUX ?
        // MARCHE PAS SI ON CHANGE LA VISIBILITE DE LA CARTE DEPUIS LA STACK
        let tmp = [...opponentStackCards]

        // Remove cards that are gone
        tmp = tmp.filter(c => props.opponentStackCards.findIndex((a) => `STACK-${a.id}` == c.id) !== -1)
        // Add new cards
        const rect = sections.sectionsDict[SECTIONS.STACK].sectionBoundingClientRect;
        props.opponentStackCards.forEach((c) => {
            if (tmp.findIndex((a) => `STACK-${c.id}` == a.id) === -1) {
                let card = JSON.parse(JSON.stringify(c))
                if (!c.id.includes("STACK-")) {
                    card.id = "STACK-" + c.id
                }
                card.position.pos = {
                    left: rect.left,
                    top: rect.top
                }
                tmp.push(card)
            }
        })
        setOpponentStackCards(tmp)
        // A voir si on peut pas supprimer un des useEffect
    }, [props.opponentStackCards.length])

    function updateSectionsClientRect() {
        // update ref bounding rect
        if (!sections) { return }
        let newSections = { ...sections }
        delete newSections.shouldUpdateClientRect
        delete sections.shouldUpdateClientRect
        newSections.allSections.forEach((s, i) => {
            newSections.sectionsDict[s].sectionBoundingClientRect = newSections.sectionsDict[s].sectionRef.current.getBoundingClientRect()
            sections.sectionsDict[s].sectionBoundingClientRect = newSections.sectionsDict[s].sectionBoundingClientRect
        })
        setSections(newSections)

        repositionCards(false, true)
    }

    useEffect(() => {
        updateSectionsClientRect()
    }, [cardSize])

    useEffect(() => {
        if (sections.shouldUpdateClientRect) {
            updateSectionsClientRect()
        }
    }, [sections])

    useEffect(() => {
        // TOUJOURS UTILE CA ?
        visibleCardsRef.current = visibleCards
        //sendData(visibleCardsRef.current, deck)
    }, [visibleCards, deck])

    useEffect(() => {
        sendData(visibleCardsRef.current, playerDeckRef.current, topCardHiddenTo)
        topCardHiddenToRef.current = topCardHiddenTo
    }, [topCardHiddenTo])

    useEffect(() => {
        playerDeckRef.current = deck
        // A VERIFIER
        let newTopCardHiddenTo = topCardHiddenTo
        if (newTopCardHiddenTo["KEEP"] !== true) {
            const tmp = [...props.opponentIds, props.playerId]
            newTopCardHiddenTo = {}
            tmp.forEach(id => {
                newTopCardHiddenTo[id] = true
            })
        }
        setTimeout(() => {
            setTopCardHiddenTo(newTopCardHiddenTo)
            topCardHiddenToRef.current = newTopCardHiddenTo
        }, 100)
    }, [deck, deck.length])

    useEffect(() => {
        const rafId = requestIdleCallback(() => {
            repositionCards([SECTIONS.STACK])
        });
        return () => cancelIdleCallback(rafId); // Clean up if needed
        //repositionCards([SECTIONS.STACK])
    }, [opponentStackCards.length, props.stackOrder])

    useEffect(() => {
        // Why not in [] ?
        if (gameCards) {
            let tokens = []
            Object.entries(gameCards).map(([k, v]) => {
                if (v.isToken) {
                    tokens.push({ ...v })
                }
            })
            setAvailableTokens(tokens)
        }
    }, [gameCards])
    /*
        useEffect(() => {
            const rafId = requestIdleCallback(() => {
                if (sectionsCardsRef.current["Mana"] && sectionsCardsRef.current["Mana"].length > 0) {
                    // Cancel highlight
                    sectionsCardsRef.current["Mana"].forEach((c, i) => {
                        delete c.card.isManaCostHightlight
                    })
    
                    // Add highlight
                    if (props.options.costAutoPay) {
                        let manaHighlight = 0
                        sectionsCardsRef.current["Mana"].forEach((c, i) => {
                            if (!c.card.isTapped && manaHighlight < hoverCardCost) {
                                c.card.isManaCostHightlight = true
                                manaHighlight += 1
                            }
                        })
                    }
                    repositionCards(["Mana"], true)
                }
            });
    
            return () => cancelIdleCallback(rafId); // Clean up if needed
        }, [hoverCardCost])
    */
    useEffect(() => {
        if (props.currentActionMode.action === ACTION_MODES.GROUPPING_TO_OPPONENT) {
            const cardId = props.currentActionMode.actionData.cardToGroupIndex
            visibleCardsRef.current[cardId].grouppingTarget = props.currentActionMode.actionData.grouppingTarget
            visibleCardsRef.current[cardId].hiddenTo = {}
            giveCardAtIndexTo(cardId, props.currentActionMode.actionData.opponentId)
            props.setCurrentActionMode({
                action: ACTION_MODES.NONE,
                actionData: false
            })
        }
    }, [props.currentActionMode])

    /*-------------------------- PLAYER SECTION FUNCTIONS --------------------------*/

    function sendData(vc, d, dt) {
        props.sendData({
            visibleCards: vc,
            deck: d,
            deckTopCardHiddenTo: dt
        })
    }

    useEffect(() => {
        if ((props.isYourTurn || (props.gameOptions && props.gameOptions.format.newTurn.sharedTurn)) && !props.isEliminated) {
            if (!props.isPausingBeforeUpkeep.val && props.isPausingBeforeUpkeep.lastTurn <= props.turnCount) {
                startingYourTurn()
            }
        }
    }, [props.isPausingBeforeUpkeep])

    function newTurn() {
        // Only during your turn
        if (props.isYourTurn || props.gameOptions.format.newTurn.sharedTurn) {
            const shouldStartNewTurnNow = !props.isEliminated && !props.isPausingBeforeUpkeep.val
            untapStep(!shouldStartNewTurnNow)
            if (shouldStartNewTurnNow) {
                startingYourTurn()
            }
        }

        // When any player turn end
        visibleCardsRef.current.forEach((c, i) => {
            delete c.isPresented
        })
        setTimeout(() => {
            // TODO: Remove the timeout
            props.setCardsLinks([])
        }, 100)
    }

    function untapStep(shouldReposition) {
        visibleCardsRef.current.forEach((c, i) => {
            let cardSection = c.position.section
            if (!cardSection && c.grouppedToId) {
                const parentIndex = visibleCardsRef.current.findIndex((a) => a.id === c.grouppedToId)
                if (parentIndex !== -1) {
                    cardSection = visibleCardsRef.current[parentIndex].position.section
                }
            }
            if (cardSection && !sections.sectionsDict[cardSection].keepTappedNewTurn) {
                c.isTapped = false
            }
        })
        props.removeTemporaryTokens()
        if (shouldReposition) {
            repositionCards()
        }
    }

    function startingYourTurn() {
        if (props.gameOptions && props.gameOptions.format.newTurn.drawPerTurn) {
            draw(props.gameOptions.format.newTurn.drawPerTurn)
        } else {
            draw(1)
        }
    }

    const moveCard = useCallback((card, targetSection) => {
        const getCardIndex = () => {
            return visibleCardsRef.current.findIndex((vc) => vc.id === card.id)
        }
        let sectionData = sections.sectionsDict[targetSection]
        if (!sectionData) { return }
        let isHidden = sectionData.isHidden
        let isCardCurrentlyHidden = isCardHidden(card, props.playerId, props.opponentIds)
        let shouldCardAppearInHistory = true
        if (isCardCurrentlyHidden === "yes" || isHidden === "yes") {
            shouldCardAppearInHistory = sectionData.showHiddenCardInHistory
        }
        if (isCardCurrentlyHidden == "yes" && card.position.section != SECTIONS.DECK && card.position.section != SECTIONS.DISCARD && card.position.section != SECTIONS.EXILE) {
            isHidden = "yes"
        }
        if (targetSection == SECTIONS.DECK || targetSection == SECTIONS.DISCARD || targetSection == SECTIONS.EXILE) {
            isHidden = "no"
        }
        if (targetSection === SECTIONS.HAND) {
            isHidden = "opponent-only"
        }

        const movedWording = () => {
            if (targetSection == SECTIONS.DISCARD) {
                return "discarded"
            } else if (targetSection == SECTIONS.EXILE) {
                return "removed"
            } else if (targetSection == SECTIONS.EXILE_HIDDEN) {
                return "removed face down"
            } else if (card.position.section == SECTIONS.DISCARD || card.position.section == SECTIONS.EXILE || card.position.section == SECTIONS.EXILE_HIDDEN || card.position.section == SECTIONS.SIDEBOARD) {
                if (targetSection == SECTIONS.HAND) {
                    return "returned"
                } else {
                    return "played"
                }
            } else if (card.position.section == SECTIONS.DECK && targetSection == SECTIONS.HAND) {
                return "drawed"
            } else {
                return "played"
            }
        }
        if (targetSection == card.position.section) {
            // Aucun historique si la carte n'a pas bougé
        } else if (card.position.section == SECTIONS.HAND) {
            props.sendToHistory(movedWording() + " from hand", shouldCardAppearInHistory ? card : false)
        } else if (card.position.section == SECTIONS.DISCARD) {
            props.sendToHistory(movedWording() + " from discard", card)
        } else if (card.position.section == SECTIONS.EXILE) {
            props.sendToHistory(movedWording() + " from remove", card)
        } else if (card.position.section == SECTIONS.EXILE) {
            props.sendToHistory(movedWording() + " from remove face down", card)
        } else if (card.position.section == SECTIONS.SIDEBOARD) {
            props.sendToHistory(movedWording() + " from sideboard", card)
        } else if (card.position.section == SECTIONS.DECK && !card.isToken) {
            if (targetSection == SECTIONS.HAND) {
                props.sendToHistory(movedWording() + " from deck")
            } else {
                props.sendToHistory(movedWording() + " from deck", shouldCardAppearInHistory ? card : false)
            }
        } else if (card.position.section != SECTIONS.STACK) {
            if (targetSection == SECTIONS.DISCARD || targetSection == SECTIONS.EXILE) {
                props.sendToHistory(movedWording(), card)
            } else if (targetSection == SECTIONS.HAND) {
                props.sendToHistory("returned to hand", shouldCardAppearInHistory ? card : false)
            } else if (targetSection == SECTIONS.EXILE_HIDDEN) {
                props.sendToHistory(movedWording(), shouldCardAppearInHistory ? card : false)
            }
        }


        // Delete if token/effect getting out of the board
        if (card.isToken) {
            if ([SECTIONS.EXILE, SECTIONS.EXILE_HIDDEN, SECTIONS.DECK, SECTIONS.HAND, SECTIONS.DISCARD].includes(targetSection)) {
                let index = getCardIndex()
                if (index != -1) {
                    visibleCardsRef.current.splice(index, 1)
                    handleGrouppedCardMove(card, sectionData)
                    return
                }
            }
        }
        if (card.isTriggerEffect && targetSection != SECTIONS.STACK) {
            let index = getCardIndex()
            if (index != -1) {
                visibleCardsRef.current.splice(index, 1)
                return
            }
        }

        let w = sectionData.cardWidth
        let h = sectionData.cardHeight

        const isCardHorizontal = (card.isFlipped ? card.cardData.face.back.isHorizontal : card.cardData.face.front.isHorizontal) && sectionData.isHorizontalAllowed
        const cardSize = {
            width: isCardHorizontal ? h : w,
            height: isCardHorizontal ? w : h
        }

        const originalSection = card.position.section

        // Keep position if its just a refresh
        if (originalSection == targetSection) {
            card.position = { ...card.position }
            card.position.cardSize = cardSize
        } else {
            card.position = {
                section: targetSection,
                cardSize: cardSize
            }
            if (originalSection === SECTIONS.DECK) {
                card.position.index = 9999
            }
        }
        if (targetSection != originalSection) {
            if (!(originalSection === SECTIONS.DECK && card.createDraggedEvent && targetSection !== SECTIONS.DISCARD)) {
                hideCard(card, isHidden, props.playerId, props.opponentIds)
            }
        }
        card.isHorizontal = isCardHorizontal
        if (isHidden === "yes") {
            card.isHorizontal = false
            card.position.cardSize = {
                width: w,
                height: h
            }
        }
        if (!card.isTapped) {
            card.isTapped = sectionData.enterTapped
        }
        if (!card.isSpun) {
            card.isSpun = sectionData.enterSpun
        }

        if (targetSection == SECTIONS.STACK) {
            let allCards = [...visibleCardsRef.current, ...opponentStackCards]
            let stackCount = 0
            allCards.forEach((c, i) => {
                if (c.position.section == SECTIONS.STACK) {
                    stackCount += 1
                }
            })

            card.isPresented = false
            card.stackIndex = stackCount
        } else {
            delete card.stackIndex
        }

        // Untap if its leaving the board
        let preventHandleGroup = false
        if ([SECTIONS.HAND, SECTIONS.DISCARD, SECTIONS.EXILE, SECTIONS.EXILE_HIDDEN].includes(targetSection)) {
            card.isTapped = false
            delete card.isSpun
            delete card.isPresented
            card.counters = [{}, {}]



            // Exluding exiles for now
            if (card.owner != props.playerId && targetSection != SECTIONS.EXILE && targetSection != SECTIONS.EXILE_HIDDEN) {
                card.hiddenTo = {}
                if (!card.ignoreGroupMove) {
                    handleGrouppedCardMove(card, sectionData, originalSection, true)
                }
                delete card.ignoreGroupMove
                giveCardAtIndexTo(getCardIndex(), card.owner)
                cardDraggedRef.current = false
                preventHandleGroup = true
                return
            }
        }
        if (!card.ignoreGroupMove) {
            handleGrouppedCardMove(card, sectionData, card.childrenDestination ? card.childrenDestination : originalSection)
            delete card.childrenDestination
        } else {
            delete card.ignoreGroupMove
        }



        cardDraggedRef.current = false


        // TAP MANA
        if (props.options.costAutoPay && props.currentActionMode.action != ACTION_MODES.FREE_PLAY && originalSection != targetSection && (originalSection && sections.sectionsDict[originalSection].autoPayFrom && !sectionData.noAutoPayTo) && sectionsCardsRef.current["Mana"] && sectionsCardsRef.current["Mana"].length > 0) {
            let manaTapped = 0
            sectionsCardsRef.current["Mana"].sort((a, b) => a.card.position.index - b.card.position.index)
            sectionsCardsRef.current["Mana"].forEach((c, i) => {
                if (!c.card.isTapped && manaTapped < card.cardData.cost) {
                    c.card.isTapped = true
                    manaTapped += 1
                }
            })
            /*
                        visibleCardsRef.current.forEach((c, i) => {
                            if (manaToTap.includes(c.id)) {
                                console.log("tapping")
                                visibleCardsRef.current[i].isTapped = true
                            }
                        })*/
        }
        setHoverCardCost(0)
        //sections.sectionsCards["Mana"][0].isTapped = true
    }, [
        //props.playerId,
        //props.opponentIds,
        props.sendToHistory,
        props.options?.costAutoPay,
        props.currentActionMode?.action,
        sections, // if sections is a prop or stable reference
        opponentStackCards,
        cardDraggedRef,
        visibleCardsRef,
        sectionsCardsRef,
        topCardHiddenTo
        //handleGrouppedCardMove,
        //giveCardAtIndexTo
    ]);

    function handleGrouppedCardMove(card, sectionData, customChildrenSection, ignoreReposition) {
        const childrenUngrouppedSection = customChildrenSection ? customChildrenSection : card.position.section
        const w = sectionData.cardWidth
        const h = sectionData.cardHeight
        const isCardHorizontal = (card.isFlipped ? card.cardData.face.back.isHorizontal : card.cardData.face.front.isHorizontal) && sectionData.isHorizontalAllowed
        const cardSize = {
            width: isCardHorizontal ? h : w,
            height: isCardHorizontal ? w : h
        }

        // If groupped to someone, remove it
        if (card.grouppedToId !== undefined) {
            let index = visibleCardsRef.current.findIndex((a) => a.id === card.grouppedToId)
            let parent = index >= 0 ? visibleCardsRef.current[index] : false
            if (parent && parent.grouppedCards) {
                let index = parent.grouppedCards.findIndex(child => child.id == card.id)
                if (index !== -1) {
                    parent.grouppedCards.splice(index, 1)
                    if (parent.grouppedCards.length == 0) {
                        delete parent.grouppedCards
                    }
                }
            }
            delete card.grouppedToId
            if (!ignoreReposition) {
                repositionCards()
            }
        }

        if (card.grouppedCards) {
            const repositionChild = (ch) => {
                const isChildHorizontal = ch.isHorizontal && sectionData.isHorizontalAllowed
                let childCardSize = {
                    width: isChildHorizontal ? h : w,
                    height: isChildHorizontal ? w : h
                }
                if (ch.position) {
                    ch.position.cardSize = childCardSize
                }
            }

            if (sectionData.isGroupForbidden) {
                card.grouppedCards.forEach((child, j) => {
                    delete child.grouppedToId
                    if (child.position) {
                        child.position.section = childrenUngrouppedSection
                        child.position.index = 99999
                    }

                    if (card.childrenDestination) {
                        repositionChild(child)
                    }
                })
                delete card.grouppedCards
            } else {
                card.grouppedCards.forEach((child, j) => {
                    repositionChild(child)
                })
            }
        }
    }

    // Reposition all cards in visibleCards
    const repositionCards = useCallback((changedSections, localOnly) => {
        if (!sections) return;
        /*
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => doReposition());
            } else {
                requestAnimationFrame(() => doReposition());
            }*/
        doReposition()

        function doReposition() {
            sectionsCardsRef.current = {};
            sections.allSections.forEach((s) => {
                sectionsCardsRef.current[s] = [];
            });

            const newVisibleCards = [...visibleCardsRef.current];
            const newOpponentStackCards = [...opponentStackCards]
            const allCards = [...newVisibleCards, ...newOpponentStackCards];

            allCards.forEach((c, i) => {
                if (c.position.section) {
                    sectionsCardsRef.current[c.position.section].push({ card: c, index: i });
                }
            });

            sections.allSections.forEach((s) => {
                if (!changedSections || changedSections.includes(s)) {
                    if (s == SECTIONS.STACK) {
                        const isHost = props.gameOptions.host === props.playerId

                        if (!isHost) {
                            sectionsCardsRef.current[s].forEach((c) => {
                                props.stackOrder.forEach((so, i) => {
                                    if (so === c.card.id || "STACK-" + so === c.card.id) {
                                        // If for upcoming stack shuffle
                                        if (c.card.stackIndex >= 0) {
                                            c.card.stackIndex = i + 1
                                        }
                                    }
                                })
                            })
                        }

                        // sort
                        let newStackOrder = []
                        sectionsCardsRef.current[s].sort((a, b) => a.card.stackIndex - b.card.stackIndex)
                        sectionsCardsRef.current[s].forEach((c, i) => {
                            c.card.stackIndex = i + 1
                            newStackOrder.push(c.card.id.replace("STACK-", ""))
                        })

                        if (isHost) {
                            if (newStackOrder.length !== props.stackOrder.length || !newStackOrder.every((val, i) => val === props.stackOrder[i])) {
                                props.updateStackOrder(newStackOrder)
                            }
                        }
                    }

                    setPositions(
                        sectionsCardsRef.current[s],
                        sections.sectionsDict[s].sectionBoundingClientRect,
                        sections.sectionsDict[s].alignment,
                        cardSize.vh
                    );

                    // Play latest logic
                    if (s === SECTIONS.STACK) {
                        let latest = false;
                        sectionsCardsRef.current[s].forEach((c) => {
                            if (c.card.position.reversedIndex === 0) {
                                const dest = sections.autoPlayFromStack[c.card.cardData.type];
                                const isOpponent = c.card.id.includes("STACK");
                                if (!isOpponent && (dest || c.card.isTriggerEffect)) {
                                    latest = c.card;
                                }
                            }
                        });
                        setLatestSackCard(latest);
                    }
                }
            });

            setSections(sections);
            visibleCardsRef.current = newVisibleCards
            setVisibleCards(visibleCardsRef.current)
            setOpponentStackCards(newOpponentStackCards)
            refreshCount.current = refreshCount.current > 1000 ? 1 : refreshCount.current + 1

            if (!localOnly) {
                sendData(visibleCardsRef.current, playerDeckRef.current, topCardHiddenToRef.current);
            }
        }
    }, [sections, opponentStackCards, props.givenCard, visibleCards, props.isEliminated]);

    const draw = useCallback((x, customDeck) => {
        let newDeck = customDeck ? [...customDeck] : [...playerDeckRef.current];
        let newCards = newDeck.splice(-x);
        setDeck(newDeck);
        playerDeckRef.current = newDeck

        const rect = sections.sectionsDict[SECTIONS.DECK].sectionBoundingClientRect;
        const handSectionData = sections.sectionsDict[SECTIONS.HAND];

        newCards.forEach((c, i) => {
            let newCard = { ...c };
            newCard.position = {
                section: SECTIONS.DECK,
                pos: { left: rect.left, top: rect.top },
                cardSize: {
                    width: handSectionData.cardWidth,
                    height: handSectionData.cardHeight
                }
            };
            hideCard(newCard, "yes", props.playerId, props.opponentIds);

            visibleCardsRef.current.push(newCard);
            newCards[i] = newCard;
        });
        repositionCards([SECTIONS.DECK]);

        // Delay the move to hand for smoother transition
        setTimeout(() => {
            newCards.forEach((c) => {
                hideCard(c, "opponent-only", props.playerId, props.opponentIds);
                c.position.section = SECTIONS.HAND;
                c.position.index = 9999
            });

            // Ensure layout updates after cards move
            repositionCards([SECTIONS.HAND]);
        }, 100);

        if (newCards.length > 0) {
            props.sendToHistory(`draw ${newCards.length}`);
        }
    }, [deck, sections, props.playerId, props.opponentIds, props.sendToHistory, opponentStackCards]);

    const createDraggedCard = useCallback((c, event) => {
        let rect = sections.sectionsDict[SECTIONS.DECK].sectionBoundingClientRect;
        let handSectionData = sections.sectionsDict[SECTIONS.HAND];

        let newCard = { ...c };
        newCard.id = uuidv4();
        newCard.position = {
            section: SECTIONS.DECK,
            pos: { left: rect.left, top: rect.top },
            cardSize: {
                width: handSectionData.cardWidth,
                height: handSectionData.cardHeight
            }
        };
        newCard.createDraggedEvent = event;
        newCard.hiddenTo = JSON.parse(JSON.stringify(topCardHiddenTo));

        visibleCardsRef.current.push(newCard);
        cardDraggedRef.current = newCard;
        repositionCards([SECTIONS.DECK]);
    }, [sections, topCardHiddenTo, repositionCards]);

    function clickCard(cardIndex) {
        let card = visibleCardsRef.current[cardIndex]
        if (!card) { return }
        const cardSection = card.position.section
        if (props.currentActionMode.action == ACTION_MODES.PING) {
            // PING
            props.pingCard(card)
        } else if (props.currentActionMode.action == ACTION_MODES.LINK_FROM || props.currentActionMode.action == ACTION_MODES.LINK_TO) {
            // LINK BETWEEN CARDS
            if (!props.currentActionMode.actionData) {
                props.setCurrentActionMode({
                    action: ACTION_MODES.LINK_TO,
                    actionData: {
                        id: uuidv4(),
                        fromCardName: card.cardData.name,
                        fromSection: card.position.section,
                        from: card.id,
                        to: false,
                        toSection: false
                    }
                })
            } else {
                let linkData = props.currentActionMode.actionData
                linkData.to = card.id
                linkData.toSection = card.position.section
                let tmp = [...props.cardsLinks]
                tmp.push(linkData)
                props.setCardsLinks(tmp)
                if (linkData.isFromQuickAction) {
                    props.setCurrentActionMode({
                        action: ACTION_MODES.NONE,
                        actionData: false
                    })
                } else {
                    props.setCurrentActionMode({
                        action: ACTION_MODES.LINK_FROM,
                        actionData: false
                    })
                }
            }
        } else if (props.currentActionMode.action == ACTION_MODES.PRESENT) {
            // PRESENT CARD TO OPPOENENTS
            if (![SECTIONS.HAND, SECTIONS.DISCARD, SECTIONS.EXILE, SECTIONS.STACK].includes(card.position.section)) {
                card.isPresented = !card.isPresented
                repositionCards([cardSection])
            }
        } else if (props.currentActionMode.action == ACTION_MODES.DISCARD) {
            // DISCARD
            moveCard(card, SECTIONS.DISCARD)
            repositionCards([cardSection, SECTIONS.DISCARD])
        } else if (props.currentActionMode.action == ACTION_MODES.HAND) {
            // HAND
            moveCard(card, SECTIONS.HAND)
            repositionCards([cardSection, SECTIONS.HAND])
        } else if (props.currentActionMode.action == ACTION_MODES.REMOVE) {
            // REMOVE
            moveCard(card, SECTIONS.EXILE)
            repositionCards([cardSection, SECTIONS.EXILE])
        } else if (props.currentActionMode.action == ACTION_MODES.COUNTERS_PRIMARY_ADD) {
            // COUNTERS ADD
            card.counters[0] = {
                value: card.counters[0].value === undefined ? 1 : Number(card.counters[0].value) + 1
            }
            repositionCards([cardSection])
        } else if (props.currentActionMode.action == ACTION_MODES.COUNTERS_SECONDARY_ADD) {
            // COUNTERS ADD
            card.counters[1] = {
                value: card.counters[1].value === undefined ? 1 : Number(card.counters[1].value) + 1
            }
            repositionCards([cardSection])
        } else if (props.currentActionMode.action == ACTION_MODES.CARD_EFFECT) {
            // CARD EFFECT
            let c = JSON.parse(JSON.stringify(card))
            c.id = uuidv4()
            c.isTriggerEffect = true
            delete c.tokenCount
            delete c.counters
            let tmp = [...visibleCardsRef.current]
            tmp.push(c)
            setVisibleCards(tmp)
            setTimeout(() => {
                moveCard(c, SECTIONS.STACK)
                repositionCards([cardSection, SECTIONS.STACK])
            }, 100)
        } else if (props.currentActionMode.action == ACTION_MODES.HIDE_REVEAL) {
            // HIDE REVEAL
            let sectionData = sections.sectionsDict[cardSection]
            let defaultRevealedState = "no"
            if (sectionData) {
                defaultRevealedState = sectionData.isHidden === "opponent-only" ? "opponent-only" : "no"
            }
            swapHideOrRevealCard(card, props.playerId, props.opponentIds, defaultRevealedState)
            repositionCards()
        } else if (props.currentActionMode.action == ACTION_MODES.SPIN) {
            // HIDE REVEAL
            card.isSpun = !card.isSpun
            repositionCards()
        } else if (card.position.section == SECTIONS.HAND) {
            let type = card.isFlipped ? card.cardData.face.back.type : card.cardData.face.front.type
            let destination = sections.autoPlayFromHand[type]
            if (props.options.autoPlayToPile) {
                destination = SECTIONS.STACK
            }
            if (destination) {
                if (destination == ACTION_MODES.GROUP) {
                    props.setCurrentActionMode({
                        action: ACTION_MODES.GROUP,
                        actionData: {
                            cardToGroupIndex: cardIndex,
                            cardName: card.cardData.name
                        }
                    })
                } else {
                    moveCard(card, destination)
                    repositionCards([destination, SECTIONS.HAND])
                    props.setCurrentActionMode({
                        action: ACTION_MODES.NONE,
                        actionData: false
                    })
                }
            }
        } else if (card.position.section == SECTIONS.STACK) {

        } else if (card.position.section == SECTIONS.DISCARD) {
        } else {
            // PASSER EN REF
            if (props.currentActionMode.action == ACTION_MODES.GROUP) {
                let childCard = visibleCardsRef.current[props.currentActionMode.actionData.cardToGroupIndex]
                groupCard(card, childCard)
            } else {
                visibleCardsRef.current[cardIndex].isTapped = !visibleCardsRef.current[cardIndex].isTapped
                repositionCards()
                /*
                visibleCardsRef.current[cardIndex].position.cardSize = {
                    width: visibleCardsRef.current[cardIndex].position.cardSize.height,
                    height: visibleCardsRef.current[cardIndex].position.cardSize.width
                }*/
            }
        }
    }

    function groupCard(parentCard, childCard) {
        if (childCard.position.section === SECTIONS.HAND) {
            let isCardCurrentlyHidden = isCardHidden(childCard, props.playerId, props.opponentIds)
            if (isCardCurrentlyHidden === "opponent-only") {
                childCard.hiddenTo = {}
            }
        }

        if (childCard.id == parentCard.id) { return } // Pas de groupe avec sois même
        if (parentCard.grouppedCards) {
            let alreadyAChildren = false
            parentCard.grouppedCards.forEach((child) => {
                if (child.id == childCard.id) {
                    alreadyAChildren = true
                }
            })
            if (alreadyAChildren) { return }
        }
        if (!parentCard.position.section) { return }

        handleGrouppedCardMove(childCard, sections.sectionsDict[parentCard.position.section])

        // A VOIR POUR HORIZONTAL
        const cardSize = {
            width: parentCard.isHorizontal != childCard.isHorizontal ? parentCard.position.cardSize.height : parentCard.position.cardSize.width,
            height: parentCard.isHorizontal != childCard.isHorizontal ? parentCard.position.cardSize.width : parentCard.position.cardSize.height
        }

        childCard.position = {
            section: false,
            cardSize: cardSize
        }

        childCard.grouppedToId = parentCard.id

        if (parentCard.grouppedCards) {
            parentCard.grouppedCards.push(childCard)
        } else {
            parentCard.grouppedCards = [childCard]
        }

        props.setCurrentActionMode({
            action: ACTION_MODES.NONE,
            actionData: false
        })
        repositionCards()
    }

    function createToken(c) {
        let rect = sections.sectionsDict[SECTIONS.DECK].sectionBoundingClientRect
        let handSectionData = sections.sectionsDict[SECTIONS.DECK]
        let newCard = createGameCardFromCardData(c)
        newCard.isToken = true
        newCard.tokenCount = 1

        newCard.position = {
            section: SECTIONS.DECK,
            pos: { left: rect.left, top: rect.top },
            cardSize: {
                width: handSectionData.cardWidth,
                height: handSectionData.cardHeight
            }
        }
        hideCard(newCard, "yes", props.playerId, props.opponentIds)

        visibleCardsRef.current.push(newCard)

        setTimeout(() => {
            let destination = sections.autoPlayFromStack[newCard.cardData.type]
            destination = destination ? destination : SECTIONS.STACK
            if (destination === "GROUP") {
                destination = SECTIONS.STACK
            }
            moveCard(newCard, destination)
            repositionCards([destination])
        }, 100)

        props.sendToHistory("created token " + newCard.cardData.name)
    }

    const giveCardAtIndexTo = useCallback((cardIndex, destination, section) => {
        // Give from deck
        if (section === SECTIONS.EXILE_HIDDEN) {
            let newDeck = [...playerDeckRef.current];
            const card = newDeck.splice(-1)[0];
            setDeck(newDeck);
            playerDeckRef.current = newDeck

            card.position.section = SECTIONS.EXILE_HIDDEN
            props.giveCardTo(card, destination)
            repositionCards()
            return
        }

        let index = cardIndex
        if (section !== undefined) {
            index = visibleCardsRef.current.findIndex((a) => a.id === sectionsCardsRef.current[section][cardIndex].card.id)
        }
        if (index === -1) { return }
        let card = visibleCardsRef.current[index]
        if (!card) { return }
        props.giveCardTo(card, destination)
        visibleCardsRef.current.splice(index, 1)

        repositionCards()
    }, [props.giveCardTo, repositionCards]);


    const allSectionToDeck = useCallback((section) => {
        const newDeck = [...playerDeckRef.current]
        const discardCount = visibleCardsRef.current.filter(
            card => card.position.section === section
        ).length;

        if (discardCount === 0) { return }

        visibleCardsRef.current = visibleCardsRef.current.filter(card => {
            if (card.position.section === section) {
                card.position.section = SECTIONS.DECK
                newDeck.push(card);
                return false;
            }
            return true;
        });

        props.sendToHistory("Shuffled " +  discardCount + " cards into their deck")
        shuffleArray(newDeck)
        playerDeckRef.current = newDeck
        setDeck(newDeck)
        repositionCards()
    }, [props.sendToHistory, setDeck, repositionCards]);



    if (!sections) {
        return (
            <div className={"d-flex flex-column player-section w-100 h-50 position-relative" + (props.isYourTurn ? " current-player" : "")}>
                {props.profileData && (<div id={"playmat-" + props.playerId} className="background player-playmat position-absolute start-0 top-0 w-100 h-100" style={{ backgroundImage: "url(" + props.profileData.background + ")" }}></div>)}
            </div>
        )
    }

    return (
        <div className={"d-flex flex-column player-section w-100 h-50 position-relative" + (props.isYourTurn ? " current-player" : "") + (props.isEliminated ? " eliminated" : "")} >
            {props.profileData && (<div id={"playmat-" + props.playerId} className="background player-playmat position-absolute start-0 top-0 w-100 h-100" style={{ backgroundImage: "url(" + props.profileData.background + ")" }}></div>)}
            <div className="position-relative d-flex flex-column w-100 h-100 justify-content-after">
                <div style={{ height: '38vh' }}>
                    <LayoutBoardSection
                        sectionLayout={sections.layout}
                        cardDraggedRef={cardDraggedRef}
                        moveCard={moveCard}
                        sections={sections}
                        sectionsCardsRef={sectionsCardsRef}
                        repositionCards={repositionCards}
                        sendToHistory={props.sendToHistory}
                        playerId={props.playerId}
                        opponentIds={props.opponentIds}
                    />
                </div>
                <Hand
                    cardDraggedRef={cardDraggedRef}
                    moveCard={moveCard}
                    handRef={handRef}
                    repositionCards={repositionCards}
                    sectionCards={sectionsCardsRef.current[SECTIONS.HAND]}
                    sendToHistory={props.sendToHistory}
                    playerId={props.playerId}
                    opponentIds={props.opponentIds}
                />
                <Deck
                    deck={deck}
                    setDeck={setDeck}
                    deckRef={deckRef}
                    draw={draw}
                    repositionCards={repositionCards}
                    moveCard={moveCard}
                    sections={sections}
                    visibleCardsRef={visibleCardsRef}
                    sendToHistory={props.sendToHistory}
                    playerId={props.playerId}
                    opponentIds={props.opponentIds}
                    currentActionMode={props.currentActionMode}
                    opponentsData={props.opponentsData}
                    setFocusedCard={props.setFocusedCard}
                    topCardHiddenTo={topCardHiddenTo}
                    setTopCardHiddenTo={setTopCardHiddenTo}
                    setIsTextInputFocused={props.setIsTextInputFocused}
                    createDraggedCard={createDraggedCard}
                    giveCardAtIndexTo={giveCardAtIndexTo}
                />
                <Discard
                    cardDraggedRef={cardDraggedRef}
                    discardRef={discardRef}
                    moveCard={moveCard}
                    repositionCards={repositionCards}
                    sectionCards={sectionsCardsRef.current[SECTIONS.DISCARD]}
                    exileSectionCard={sectionsCardsRef.current[SECTIONS.EXILE]}
                    exileHiddenSectionCard={sectionsCardsRef.current[SECTIONS.EXILE_HIDDEN]}
                    opponentIds={props.opponentIds}
                    opponentsData={props.opponentsData}
                    giveCardAtIndexTo={giveCardAtIndexTo}
                    sideboard={sideboard}
                    setSideboard={setSideboard}
                    visibleCardsRef={visibleCardsRef}
                    allSectionToDeck={allSectionToDeck}
                />
                <Stack
                    cardDraggedRef={cardDraggedRef}
                    stackRef={props.stackRef}
                    moveCard={moveCard}
                    repositionCards={repositionCards}
                    stackCards={sectionsCardsRef.current[SECTIONS.STACK]}
                    playerId={props.playerId}
                    latestStackCard={latestStackCard}
                    sections={sections}
                    displayedTitle={sections.sectionsDict[SECTIONS.STACK].displayedTitle}
                    setCurrentActionMode={props.setCurrentActionMode}
                    visibleCards={visibleCards}
                />
            </div>
            <div className='visible-cards position-fixed start-0 top-0 w-100 h-100 pe-none'>
                {visibleCards.map((c, i) => {
                    return (<Card
                        key={c.id}
                        card={c}
                        cardDraggedRef={cardDraggedRef}
                        clickCard={() => clickCard(i)}
                        deck={deck}
                        deckRef={playerDeckRef}
                        cards={visibleCardsRef.current}
                        index={i}
                        repositionCards={repositionCards}
                        setCurrentActionMode={props.setCurrentActionMode}
                        setFocusedCard={props.setFocusedCard}
                        opponentIds={props.opponentIds}
                        opponentsData={props.opponentsData}
                        giveCardAtIndexTo={giveCardAtIndexTo}
                        moveCard={moveCard}
                        setHoverCardCost={() => { }}
                        currentActionMode={props.currentActionMode}
                        sendToHistory={props.sendToHistory}
                        playerId={props.playerId}
                        setIsTextInputFocused={props.setIsTextInputFocused}
                        gameOptions={props.gameOptions}
                        sectionData={sections.sectionsDict[c.position.section]}
                        handSize={sectionsCardsRef.current[SECTIONS.HAND] ? sectionsCardsRef.current[SECTIONS.HAND].length : 0}
                        refreshCount={refreshCount.current}
                    />)
                })}
                {opponentStackCards.map((c, i) => {
                    return (<Card
                        key={c.id}
                        card={c}
                        playerId={props.playerId}
                        cardDraggedRef={cardDraggedRef}
                        clickCard={() => { }}
                        isOpponentCard={true}
                        setFocusedCard={props.setFocusedCard}
                        currentActionMode={props.currentActionMode}
                        refreshCount={refreshCount.current}
                    />)
                })}
            </div>
            <TokensModal
                createToken={createToken}
                tokens={availableTokens}
                show={props.showTokensModal}
                handleClose={() => { props.setShowTokensModal(false); props.setIsTextInputFocused(false) }}
            />
            <DeckPickerModal
                show={showDeckPickerModal}
                handleClose={() => { setShowDeckPickerModal(false) }}
                currentGame={currentGame}
                gameData={gameData}
                createGameCardFromCardData={createGameCardFromCardData}
                sendToHistory={props.sendToHistory}
                start={(newDeck, handSize, cardsOnBoard, newSideboard) => {
                    setDeck(newDeck) // Faut que je le vire lui nan ?
                    playerDeckRef.current = newDeck
                    draw(handSize, newDeck)

                    cardsOnBoard.forEach((c, i) => {
                        let rect = sections.sectionsDict[c.section].sectionBoundingClientRect
                        let sectionData = sections.sectionsDict[c.section]

                        let newCard = { ...c.card }
                        newCard.position = {
                            section: c.section,
                            pos: { left: rect.left, top: rect.top },
                            cardSize: {
                                width: sectionData.cardWidth,
                                height: sectionData.cardHeight
                            }
                        }
                        hideCard(newCard, sectionData.isHidden, props.playerId, props.opponentIds)
                        newCard.isHorizontal = c.card.cardData.face.front.isHorizontal
                        if (newCard.isHorizontal) {
                            newCard.position.cardSize = {
                                width: sectionData.cardHeight,
                                height: sectionData.cardWidth
                            }
                        }

                        visibleCardsRef.current.push(newCard)
                        cardsOnBoard[i] = newCard
                    })

                    setSideboard(newSideboard)

                    repositionCards()

                    setTimeout(() => {
                        sendData(visibleCardsRef.current, newDeck, topCardHiddenToRef.current)
                    }, 100)
                }}
                started={() => {
                    if (props.gameOptions && props.gameOptions.format.newTurn.drawOnStart) {
                        newTurn()
                    }
                }}
                gameOptions={props.gameOptions}
            />
        </div>
    )
}